/**
 * MemberDetailPanel — right-anchored panel, opens when a GM clicks a status badge.
 * Shell: §1 side-panel-design-system.md
 * Header: §2b identity header (single row only)
 * Tabs: §3 — top of body, blue gradient active
 * Body: §4 — bg-slate-50
 * Action tiles: §5
 * Footer: §6 — grid grid-cols-2, h-10 buttons
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Pause, RotateCcw, Mail, PenLine, LayoutGrid, UserPlus, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClubAdmin } from '@/hooks/useClubAdmin';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { leagueThemes } from '@/utils/leagueThemes';
import { atRiskReasonLabel } from '@/utils/at-risk-rules';
import { getAvatarGradient, getInitials } from './avatar-utils';
import { MemberActionTimeline } from './MemberActionTimeline';
import {
  ReEngagementSheet,
  LogFollowUpSheet,
  CompSomethingSheet,
  ReassignCoachSheet,
  SnoozeSheet,
  UnsnoozeSheet,
} from './MemberActionConfirmSheet';
import type { ActionSheetMember } from './MemberActionConfirmSheet';
import type { MemberProgressSummary } from '@/types/crm.types';

interface MemberDetailPanelProps {
  member: MemberProgressSummary | null;
  onClose: () => void;
}

type PanelTab = 'history' | 'send';
type SendVariant = 're_engagement' | 'log_followup' | 'comp_something' | 'reassign_coach' | 'snooze' | 'unsnooze' | null;

// §5 action tile colours
const TILE_COLORS: Record<string, string> = {
  mail:      '#2A4494',
  penline:   '#0891B2',
  layoutgrid:'#52B788',
  userplus:  '#7C3AED',
};

export const MemberDetailPanel: React.FC<MemberDetailPanelProps> = ({ member, onClose }) => {
  const { t } = useAppTranslations();
  const { currentClub } = useClubAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState<PanelTab>('history');
  const [sendVariant, setSendVariant] = useState<SendVariant>(null);

  if (!member || !currentClub) return null;

  const name = member.profile.full_name;
  const initials = getInitials(name);
  const avatarBg = member.profile.avatar_url ? undefined : getAvatarGradient(name);
  const tierName = leagueThemes[member.tier]?.name || member.tier;
  const isAtRisk = member.status === 'at_risk';
  const isSnoozed = !!member.snooze?.isActive;

  // §2b subtitle line — inline stats separated by ·
  const daysInactive = member.last_active
    ? Math.floor((Date.now() - new Date(member.last_active).getTime()) / 86_400_000)
    : null;
  const subtitleParts: Array<React.ReactNode> = [tierName];
  if (daysInactive !== null) {
    subtitleParts.push(
      <span key="inactive" style={{ color: isAtRisk ? '#EF4444' : undefined }}>
        {daysInactive}{t.crm.memberPanel.inactive}
      </span>
    );
  }
  if (member.onboarding_data) {
    subtitleParts.push(`Stage ${member.onboarding_data.current_stage_number}/9`);
    if (!member.onboarding_data.assigned_coach) {
      subtitleParts.push(<span key="nocoach" className="text-amber-500">{t.crm.memberPanel.noCoach}</span>);
    }
  }

  const actionSheetMember: ActionSheetMember = {
    id: member.member_id,
    name,
    initials,
    coachName: member.onboarding_data?.assigned_coach?.full_name,
  };

  const closeAndReset = () => { setSendVariant(null); setTab('history'); onClose(); };

  const renderSendBody = () => {
    if (sendVariant === 're_engagement')  return <ReEngagementSheet  member={actionSheetMember} onClose={closeAndReset} />;
    if (sendVariant === 'log_followup')   return <LogFollowUpSheet   member={actionSheetMember} onClose={closeAndReset} />;
    if (sendVariant === 'comp_something') return <CompSomethingSheet member={actionSheetMember} onClose={closeAndReset} />;
    if (sendVariant === 'reassign_coach') return <ReassignCoachSheet member={actionSheetMember} onClose={closeAndReset} />;
    if (sendVariant === 'snooze')         return <SnoozeSheet        member={actionSheetMember} onClose={closeAndReset} />;
    if (sendVariant === 'unsnooze')       return <UnsnoozeSheet      member={actionSheetMember} onClose={closeAndReset} />;
    return null;
  };

  const panel = (
    <>
      {/* §1 backdrop */}
      <div className="fixed inset-0 z-30" onClick={onClose} />

      {/* §1 shell */}
      <div
        className="fixed right-0 sm:right-6 top-0 sm:top-6 bottom-0 sm:bottom-6 w-full sm:w-[420px] bg-white border-0 sm:border border-slate-200 rounded-none sm:rounded-2xl flex flex-col z-[55]"
        style={{ boxShadow: '-20px 0 60px rgba(15,23,42,0.12)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Sub-sheet full-bleed overlay */}
        {sendVariant && renderSendBody()}

        {!sendVariant && (
          <>
            {/* §2b identity header — single row */}
            <div className="px-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))', paddingBottom: '1rem' }}>
              <div className="flex items-center gap-3">
                {/* Avatar — borderRadius inline per spec */}
                {member.profile.avatar_url ? (
                  <img
                    src={member.profile.avatar_url}
                    alt=""
                    className="w-9 h-9 object-cover flex-shrink-0"
                    style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 0 0 2px rgba(42,68,148,0.12), 0 2px 6px rgba(15,23,42,0.10)' }}
                  />
                ) : (
                  <div
                    className="w-9 h-9 flex items-center justify-center text-white text-[12px] font-extrabold flex-shrink-0"
                    style={{ borderRadius: 10, background: avatarBg, boxShadow: '0 0 0 2px rgba(42,68,148,0.12), 0 2px 6px rgba(15,23,42,0.10)' }}
                  >
                    {initials}
                  </div>
                )}

                {/* Identity */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[15px] font-bold text-slate-900 truncate leading-tight">{name}</span>
                    {/* §2b status badge inline */}
                    {isAtRisk && !isSnoozed && (
                      <span
                        className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold text-red-600 bg-red-50 border border-red-100"
                        style={{ borderRadius: 6 }}
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        {t.crm.memberPanel.atRisk}
                      </span>
                    )}
                    {isSnoozed && (
                      <span
                        className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold text-amber-700"
                        style={{ borderRadius: 6, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}
                      >
                        {t.crm.memberPanel.snoozed}
                      </span>
                    )}
                  </div>
                  {/* §2b subtitle line — inline · separated */}
                  <div className="text-[11px] text-slate-400 leading-tight mt-0.5 flex items-center gap-1 flex-wrap">
                    {subtitleParts.map((part, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="text-slate-300">·</span>}
                        {part}
                      </React.Fragment>
                    ))}
                    {isAtRisk && member.at_risk_reasons && member.at_risk_reasons.length > 0 && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-red-400">{member.at_risk_reasons.map(atRiskReasonLabel).join(', ')}</span>
                      </>
                    )}
                    {isSnoozed && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-amber-500">
                          {t.crm.memberPanel.until} {new Date(member.snooze!.snoozeUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Close — §2b */}
                <button
                  onClick={onClose}
                  className="flex-shrink-0 inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 transition-colors tap-target"
                  style={{ width: 28, height: 28, borderRadius: 8 }}
                  aria-label="Close panel"
                >
                  <X className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </div>
            </div>

            {/* §3 tabs — top of body, NOT in header */}
            <div className="grid grid-cols-2 gap-2 px-5 pt-4 pb-3 flex-shrink-0">
              {(['history', 'send'] as PanelTab[]).map(tabKey => {
                const active = tab === tabKey;
                return (
                  <button
                    key={tabKey}
                    onClick={() => setTab(tabKey)}
                    className="h-8 tap-target text-[12px] font-semibold capitalize inline-flex items-center justify-center transition-all"
                    style={{
                      borderRadius: 10,
                      background: active ? 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' : '#E9EEF6',
                      color: active ? '#fff' : '#94A3B8',
                      boxShadow: active ? '0 2px 8px rgba(42,68,148,0.22)' : 'none',
                    }}
                  >
                    {tabKey === 'history' ? t.crm.memberPanel.history : t.crm.memberPanel.send}
                  </button>
                );
              })}
            </div>

            {/* §4 body */}
            <div className="flex-1 overflow-y-auto bg-slate-50">
              {tab === 'history' && (
                <div className="px-5 py-4">
                  <MemberActionTimeline memberId={member.member_id} clubId={currentClub.id} />
                </div>
              )}

              {tab === 'send' && (
                <div className="px-5 py-4">
                  <p
                    className="text-[10px] font-bold uppercase tracking-widest mb-3"
                    style={{ color: 'rgba(15,23,42,0.35)' }}
                  >
                    {t.crm.memberPanel.chooseAction}
                  </p>
                  <div className="space-y-2">
                    <ActionTile icon={Mail}       color={TILE_COLORS.mail}       label={t.crm.memberPanel.sendReEngagement} sub={t.crm.memberPanel.sendReEngagementSub} onClick={() => setSendVariant('re_engagement')} />
                    <ActionTile icon={PenLine}     color={TILE_COLORS.penline}    label={t.crm.memberPanel.logFollowUp}      sub={t.crm.memberPanel.logFollowUpSub}      onClick={() => setSendVariant('log_followup')} />
                    <ActionTile icon={LayoutGrid}  color={TILE_COLORS.layoutgrid} label={t.crm.memberPanel.compSomething}    sub={t.crm.memberPanel.compSomethingSub}    onClick={() => setSendVariant('comp_something')} />
                    <ActionTile icon={UserPlus}    color={TILE_COLORS.userplus}   label={t.crm.memberPanel.reassignCoach}    sub={t.crm.memberPanel.reassignCoachSub}    onClick={() => setSendVariant('reassign_coach')} />
                    {isSnoozed && (
                      <ActionTile icon={RotateCcw} color="#F59E0B" label={t.crm.memberPanel.undoSnooze} sub={t.crm.memberPanel.undoSnoozeSub} onClick={() => setSendVariant('unsnooze')} />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* §6 footer — grid grid-cols-2 */}
            <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0 grid grid-cols-2 gap-2">
              {isAtRisk && !isSnoozed ? (
                <button
                  onClick={() => { setSendVariant('snooze'); setTab('send'); }}
                  className="h-10 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors"
                  style={{
                    background: 'rgba(245,158,11,0.08)',
                    border: '1px solid rgba(245,158,11,0.30)',
                    color: '#92400E',
                  }}
                >
                  <Pause className="w-3.5 h-3.5" />
                  {t.crm.memberPanel.snooze}
                </button>
              ) : (
                <div /> /* empty cell to keep grid alignment */
              )}
              <button
                onClick={() => navigate(`/club-admin/member-progress/${member.member_id}`)}
                className="h-10 rounded-xl text-[13px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center gap-1.5 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t.crm.memberPanel.fullProfile}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  return createPortal(panel, document.body);
};

// ── Sub-components ────────────────────────────────────────────────────────────

// §5 action tile
const ActionTile: React.FC<{
  icon: React.ElementType;
  color: string;
  label: string;
  sub: string;
  onClick: () => void;
}> = ({ icon: Icon, color, label, sub, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left rounded-xl px-4 py-3 flex items-center gap-3 transition-colors hover:brightness-95"
    style={{
      background: 'white',
      border: '1px solid rgba(15,23,42,0.06)',
      boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.05)',
    }}
  >
    <div
      className="w-9 h-9 flex items-center justify-center flex-shrink-0"
      style={{ borderRadius: 10, background: `${color}14`, border: `1px solid ${color}28` }}
    >
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-semibold text-slate-900">{label}</div>
      <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>
    </div>
    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'rgba(15,23,42,0.20)' }} />
  </button>
);
